import { useMemo, useState } from 'react'
import {
  Box,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import { UserPlus } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'
import {
  assignAgentDisplayName,
  availabilityStatusColor,
  availabilityStatusLabel,
  type AssignAgentRow,
} from '@/lib/utils/assignAgents'

interface AgentAssignMenuProps {
  agents: AssignAgentRow[]
  assignedToId?: number | null
  isAssigning?: boolean
  onAssign: (userId: number | null) => void
  includeAssignToMe?: boolean
  currentUserId?: number | null
}

export function AgentAssignMenu({
  agents,
  assignedToId,
  isAssigning = false,
  onAssign,
  includeAssignToMe = false,
  currentUserId,
}: AgentAssignMenuProps) {
  const colors = useThemeColors()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase()
    if (!term) return agents
    return agents.filter((agent) => assignAgentDisplayName(agent).toLowerCase().includes(term))
  }, [agents, query])

  return (
    <Menu isLazy lazyBehavior="unmount" placement="bottom-end" strategy="fixed" closeOnSelect>
      <Tooltip label="Assign to" placement="top" hasArrow>
        <MenuButton
          as={IconButton}
          aria-label="Assign to"
          icon={<UserPlus size={18} />}
          variant="ghost"
          size="sm"
          colorScheme="gray"
          isLoading={isAssigning}
          isDisabled={isAssigning}
          onClick={(e) => e.stopPropagation()}
        />
      </Tooltip>
      <MenuList minW="240px" py={1} overflow="hidden" onClick={(e) => e.stopPropagation()}>
        <MenuItem onClick={() => onAssign(null)}>
          <Text fontSize="sm">Unassign</Text>
        </MenuItem>
        {includeAssignToMe && currentUserId != null && (
          <MenuItem
            onClick={() => onAssign(currentUserId)}
            bg={assignedToId === currentUserId ? 'blue.50' : 'transparent'}
          >
            <Text fontSize="sm">Assign to me</Text>
          </MenuItem>
        )}
        <Box px={3} py={2} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            size="sm"
            placeholder="Search agents"
            aria-label="Search agents"
            value={query}
            bg={colors.inputBg}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </Box>
        <Box maxH="240px" overflowY="auto">
          {filtered.length === 0 ? (
            <Text px={3} py={2} fontSize="sm" color="gray.500">
              No agents found
            </Text>
          ) : (
            filtered.map((agent) => {
              const name = assignAgentDisplayName(agent)
              const status = availabilityStatusLabel(agent.availability_status)
              return (
                <MenuItem
                  key={agent.id}
                  onClick={() => agent.id != null && onAssign(agent.id)}
                  bg={assignedToId === agent.id ? 'blue.50' : 'transparent'}
                >
                  <HStack spacing={2} minW={0} w="full">
                    <Tooltip label={status} placement="left" hasArrow>
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={availabilityStatusColor(agent.availability_status)}
                        flexShrink={0}
                        aria-label={status}
                      />
                    </Tooltip>
                    <Text fontSize="sm" noOfLines={1}>
                      {name}
                    </Text>
                  </HStack>
                </MenuItem>
              )
            })
          )}
        </Box>
      </MenuList>
    </Menu>
  )
}
